// /app/frontend/src/components/admin/CMSTab.jsx
// CMS Tab for Product Content Management with AI SEO

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RichTextEditor } from '../ui/rich-text-editor';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  Package, FileText, Image, Sparkles, Plus, Pencil, Trash2, 
  Save, X, ChevronDown, ChevronRight, Eye, Globe, Search,
  Loader2, Check, RefreshCw, ExternalLink, Upload, Star, GripVertical,
  Layers, FolderTree, Zap, Shield, Palette, Sun, Moon, Wind, Droplets,
  ThermometerSun, Eye as EyeIcon, Lock, Settings, Smartphone, Wifi,
  Volume2, Battery, Clock, Calendar, Home, Building, Ruler, Maximize,
  Hand, RotateCcw, Power, Lightbulb, Award, Heart, Target, Wrench,
  Brush, Grid, Layout, Frame, Square, Circle, Triangle, Hexagon,
  Monitor, Tv, Speaker, Camera, Blinds, PanelTop, PanelBottom, 
  SlidersHorizontal, Columns, Rows, AlignVerticalJustifyCenter,
  MoveVertical, MoveHorizontal, Minimize2, Maximize2, SunDim, Cloudy,
  Thermometer, Snowflake, Flame, Volume, VolumeX, ShieldCheck, Sparkles as SparklesIcon,
  FolderOpen, Play, Video, History, LayoutTemplate, Wand2, Copy, Download,
  Image as ImageIcon, Info, HelpCircle
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ImageGalleryModal from './ImageGalleryModal';
import { sanitizeSvg, sanitizeHtml } from '../../lib/sanitize';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Field help text component with info icon and tooltip
const FieldLabel = ({ label, tooltip, helpText }) => (
  <div className="flex items-center gap-1.5 mb-1">
    <Label className="mb-0">{label}</Label>
    {tooltip && (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="w-3.5 h-3.5 text-blue-500 cursor-help hover:text-blue-600 transition-colors" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs">
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
    {helpText && (
      <span className="text-[10px] text-blue-500 font-normal ml-1">→ {helpText}</span>
    )}
  </div>
);

// General icons for picker
const GENERAL_ICONS = {
  // Features & Quality
  Shield: Shield, Check: Check, Star: Star, Award: Award, Heart: Heart, Target: Target, ShieldCheck: ShieldCheck,
  // Colors & Design
  Palette: Palette, Brush: Brush, Grid: Grid, Layout: Layout, Frame: Frame, SparklesIcon: SparklesIcon,
  // Shapes
  Square: Square, Circle: Circle, Triangle: Triangle, Hexagon: Hexagon,
  // Environment
  Sun: Sun, Moon: Moon, Wind: Wind, Droplets: Droplets, ThermometerSun: ThermometerSun,
  Thermometer: Thermometer, Snowflake: Snowflake, Flame: Flame, Cloudy: Cloudy, SunDim: SunDim,
  // Control & Tech
  Zap: Zap, Power: Power, Settings: Settings, Wrench: Wrench,
  Smartphone: Smartphone, Wifi: Wifi, Monitor: Monitor, Tv: Tv, Speaker: Speaker, Camera: Camera,
  Volume2: Volume2, Volume: Volume, VolumeX: VolumeX, Battery: Battery, Lightbulb: Lightbulb,
  // Operation
  Hand: Hand, RotateCcw: RotateCcw, Eye: EyeIcon, Lock: Lock,
  // Time & Schedule
  Clock: Clock, Calendar: Calendar,
  // Space & Size
  Home: Home, Building: Building, Ruler: Ruler, Maximize: Maximize, Minimize2: Minimize2, Maximize2: Maximize2,
  // Navigation
  ChevronDown: ChevronDown, ChevronRight: ChevronRight, Plus: Plus, X: X,
  // Files
  Package: Package, FileText: FileText, Image: Image, Layers: Layers, FolderTree: FolderTree
};

// Window treatment / Shades specific icons
const SHADES_ICONS = {
  // Shade Types & Styles
  Blinds: Blinds, PanelTop: PanelTop, PanelBottom: PanelBottom,
  Columns: Columns, Rows: Rows, Layers: Layers, Grid: Grid,
  // Movement & Control
  MoveVertical: MoveVertical, MoveHorizontal: MoveHorizontal,
  AlignVerticalJustifyCenter: AlignVerticalJustifyCenter,
  SlidersHorizontal: SlidersHorizontal,
  Hand: Hand, RotateCcw: RotateCcw, Zap: Zap,
  // Light Control
  Sun: Sun, SunDim: SunDim, Moon: Moon, Eye: EyeIcon,
  // Climate & Comfort
  Thermometer: Thermometer, ThermometerSun: ThermometerSun, 
  Snowflake: Snowflake, Flame: Flame, Wind: Wind, Cloudy: Cloudy,
  // Privacy & Protection
  Shield: Shield, ShieldCheck: ShieldCheck, Lock: Lock,
  // Sound
  Volume: Volume, VolumeX: VolumeX, Volume2: Volume2,
  // Quality
  Award: Award, Star: Star, Check: Check, SparklesIcon: SparklesIcon,
  // Size & Fit
  Ruler: Ruler, Maximize: Maximize, Minimize2: Minimize2, Maximize2: Maximize2, Frame: Frame,
  // Smart Home
  Smartphone: Smartphone, Wifi: Wifi, Settings: Settings, Power: Power
};

// Combined for rendering
const AVAILABLE_ICONS = { ...GENERAL_ICONS, ...SHADES_ICONS };

// Icon Picker Component with Tabs (including Custom icons)
const IconPicker = ({ value, onChange, onClose, token }) => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('shades'); // 'shades', 'general', or 'custom'
  const [customIcons, setCustomIcons] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  
  // Fetch custom icons when tab is selected
  useEffect(() => {
    if (activeTab === 'custom' && customIcons.length === 0) {
      fetchCustomIcons();
    }
  }, [activeTab]);
  
  const fetchCustomIcons = async () => {
    setLoadingCustom(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/cms/icons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomIcons(response.data);
    } catch (error) {
      console.error('Failed to fetch custom icons:', error);
    }
    setLoadingCustom(false);
  };
  
  // Bulk upload handler
  const handleBulkUpload = async (files) => {
    if (files.length === 0) return;
    
    setUploading(true);
    const uploaded = [];
    const failed = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(`${i + 1}/${files.length}`);
      
      if (!file.name.toLowerCase().endsWith('.svg')) {
        failed.push(file.name);
        continue;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(`${API_URL}/api/admin/cms/icons`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push(response.data);
      } catch (error) {
        failed.push(file.name);
      }
    }
    
    if (uploaded.length > 0) {
      setCustomIcons(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} icon${uploaded.length > 1 ? 's' : ''} uploaded!`);
    }
    if (failed.length > 0) {
      toast.error(`${failed.length} file${failed.length > 1 ? 's' : ''} failed`);
    }
    
    setUploading(false);
    setUploadProgress('');
  };
  
  const handleUploadIcon = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.svg')) {
      toast.error('Only SVG files are allowed');
      e.target.value = '';
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/cms/icons`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setCustomIcons(prev => [...prev, response.data]);
      toast.success('Icon uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload icon');
    }
    setUploading(false);
    e.target.value = '';
  };
  
  const handleDeleteIcon = async (iconId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/cms/icons/${iconId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomIcons(prev => prev.filter(i => i.id !== iconId));
      toast.success('Icon deleted');
    } catch (error) {
      toast.error('Failed to delete icon');
    }
  };
  
  const currentIcons = activeTab === 'shades' ? SHADES_ICONS : activeTab === 'general' ? GENERAL_ICONS : null;
  const iconNames = currentIcons ? Object.keys(currentIcons) : [];
  const filtered = search 
    ? iconNames.filter(name => name.toLowerCase().includes(search.toLowerCase()))
    : iconNames;
  
  const filteredCustom = search
    ? customIcons.filter(icon => icon.name.toLowerCase().includes(search.toLowerCase()))
    : customIcons;
  
  return (
    <div className="absolute z-50 top-full left-0 mt-1 bg-white border rounded-lg shadow-xl w-96 max-h-96 overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b">
        <button
          type="button"
          onClick={() => setActiveTab('shades')}
          className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === 'shades' 
              ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' 
              : 'text-neutral-600 hover:bg-neutral-50'
          }`}
        >
          🪟 Shades
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === 'general' 
              ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' 
              : 'text-neutral-600 hover:bg-neutral-50'
          }`}
        >
          General
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('custom')}
          className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === 'custom' 
              ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500' 
              : 'text-neutral-600 hover:bg-neutral-50'
          }`}
        >
          ✨ Custom
        </button>
      </div>
      
      {/* Search */}
      <div className="p-2 border-b">
        <Input
          placeholder="Search icons..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm"
          autoFocus
        />
      </div>
      
      {/* Icons Grid - Standard Tabs */}
      {activeTab !== 'custom' && (
        <div className="p-2 max-h-48 overflow-auto">
          <div className="grid grid-cols-8 gap-1">
            {filtered.map(name => {
              const IconComponent = currentIcons[name];
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => { onChange(name); onClose(); }}
                  className={`p-2 rounded hover:bg-neutral-100 flex items-center justify-center ${value === name ? 'bg-amber-100 ring-2 ring-amber-500' : ''}`}
                  title={name}
                >
                  <IconComponent className="w-4 h-4" />
                </button>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <p className="text-sm text-neutral-500 text-center py-4">No icons found</p>
          )}
        </div>
      )}
      
      {/* Custom Icons Tab */}
      {activeTab === 'custom' && (
        <div className="p-2 max-h-56 overflow-auto">
          {/* Bulk Upload Area */}
          <div className="mb-3">
            <label 
              className={`flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-purple-50 transition-colors ${uploading ? 'opacity-50' : ''} ${dragActive ? 'bg-purple-100 border-purple-500' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={async (e) => {
                e.preventDefault();
                setDragActive(false);
                const files = Array.from(e.dataTransfer.files).filter(f => f.name.toLowerCase().endsWith('.svg'));
                if (files.length > 0) {
                  await handleBulkUpload(files);
                } else {
                  toast.error('Only SVG files are allowed');
                }
              }}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                  <span className="text-sm text-purple-700 font-medium">Uploading {uploadProgress}...</span>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-purple-600" />
                  <span className="text-sm text-purple-700 font-medium">Upload SVG Icons</span>
                  <span className="text-xs text-purple-500">Drag & drop or click to select multiple files</span>
                </>
              )}
              <input
                type="file"
                accept=".svg"
                multiple
                className="hidden"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) {
                    await handleBulkUpload(files);
                  }
                  e.target.value = '';
                }}
                disabled={uploading}
              />
            </label>
          </div>
          
          {loadingCustom ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
            </div>
          ) : filteredCustom.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {filteredCustom.map(icon => (
                <div key={icon.id} className="relative group">
                  <button
                    type="button"
                    onClick={() => { onChange(`custom:${icon.id}`); onClose(); }}
                    className={`w-full p-3 rounded border hover:bg-neutral-50 flex flex-col items-center gap-1 ${
                      value === `custom:${icon.id}` ? 'bg-purple-100 ring-2 ring-purple-500' : ''
                    }`}
                    title={icon.name}
                  >
                    <div 
                      className="w-6 h-6"
                      dangerouslySetInnerHTML={{ __html: sanitizeSvg(icon.svg_content) }}
                    />
                    <span className="text-xs text-neutral-600 truncate w-full text-center">{icon.name}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeleteIcon(icon.id); }}
                    className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    title="Delete icon"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-neutral-500">
              <p className="text-sm">No custom icons yet</p>
              <p className="text-xs mt-1">Drag & drop SVG files or click to upload</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Render icon by name (supports custom icons with "custom:" prefix)
const RenderIcon = ({ iconName, className = "w-5 h-5", customIcons = [] }) => {
  // Check if it's a custom icon
  if (iconName?.startsWith('custom:')) {
    const iconId = iconName.replace('custom:', '');
    const customIcon = customIcons.find(i => i.id === iconId);
    if (customIcon?.svg_content) {
      return (
        <span 
          className={className} 
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          dangerouslySetInnerHTML={{ __html: sanitizeSvg(customIcon.svg_content) }}
        />
      );
    }
  }
  
  // Standard icon
  const IconComponent = AVAILABLE_ICONS[iconName];
  if (IconComponent) {
    return <IconComponent className={className} />;
  }
  return <Check className={className} />;
};

// Helper for simple icon rendering (no custom support)
const renderIcon = (iconName, className = "w-5 h-5") => {
  const IconComponent = AVAILABLE_ICONS[iconName];
  if (IconComponent) {
    return <IconComponent className={className} />;
  }
  return <Check className={className} />;
};

// Sortable Item Component for drag-and-drop
const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div 
        {...attributes} 
        {...listeners}
        className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing z-10 hover:bg-neutral-100 rounded-l-lg"
      >
        <GripVertical className="w-4 h-4 text-neutral-400" />
      </div>
      <div className="pl-8">
        {children}
      </div>
    </div>
  );
};

const CMSTab = ({ token }) => {
  const [activeView, setActiveView] = useState('products'); // 'products', 'pages', 'categories'
  const [products, setProducts] = useState([]);
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingSEO, setGeneratingSEO] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [generatingAIImage, setGeneratingAIImage] = useState(false);
  const [showAIImageModal, setShowAIImageModal] = useState(false);
  const [aiImageSettings, setAiImageSettings] = useState({
    room_type: 'living room',
    style: 'modern',
    color_scheme: ''
  });
  const [stylePresets, setStylePresets] = useState({});
  const [generatingBatch, setGeneratingBatch] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [showCategoryAIModal, setShowCategoryAIModal] = useState(false);
  const [selectedCategoryForAI, setSelectedCategoryForAI] = useState(null);
  const [generatingVariation, setGeneratingVariation] = useState(null);
  // New AI Features
  const [roomTemplates, setRoomTemplates] = useState({});
  const [promptHistory, setPromptHistory] = useState([]);
  const [showPromptHistoryModal, setShowPromptHistoryModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [comparisonImages, setComparisonImages] = useState([]);
  const [generatingAutoVariations, setGeneratingAutoVariations] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Image preview modal
  const [previewImage, setPreviewImage] = useState(null);
  const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
  // Category AI generation - full modal like products
  const [generatingCategoryAI, setGeneratingCategoryAI] = useState(false);
  const [showCategoryAIFullModal, setShowCategoryAIFullModal] = useState(false);
  const [generatingCategoryBatch, setGeneratingCategoryBatch] = useState(false);
  const [generatingCategoryAutoVar, setGeneratingCategoryAutoVar] = useState(false);
  const [categoryComparisonImages, setCategoryComparisonImages] = useState([]);
  const [showCategoryComparisonModal, setShowCategoryComparisonModal] = useState(false);
  // Brand colors lock
  const [brandColors, setBrandColors] = useState({
    enabled: false,
    primary: '#1e3a5f',
    secondary: '#f5f5f5',
    accent: '#c9a050'
  });
  const [showBrandColorModal, setShowBrandColorModal] = useState(false);
  // Custom icons for rendering
  const [customIconsCache, setCustomIconsCache] = useState([]);
  // Icon picker state
  const [activeIconPicker, setActiveIconPicker] = useState(null); // { type: 'feature'|'os', index: number }
  // Image gallery modal state
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryCallback, setGalleryCallback] = useState(null);
  const [galleryCategory, setGalleryCategory] = useState('general');
  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    features: true,
    operatingSystems: false,
    benefits: false,
    images: true,
    seo: false,
    configurator: false
  });
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  // Handler for opening image gallery with callback and optional category
  const openImageGallery = (callback, category = 'general') => {
    setGalleryCallback(() => callback);
    setGalleryCategory(category);
    setShowImageGallery(true);
  };
  
  // Drag end handler for reordering
  const handleDragEnd = (event, arrayKey) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setSelectedItem(prev => {
        const items = prev[arrayKey] || [];
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return { ...prev, [arrayKey]: arrayMove(items, oldIndex, newIndex) };
      });
    }
  };
  
  // Fetch custom icons for cache
  useEffect(() => {
    const fetchCustomIcons = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/cms/icons`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomIconsCache(response.data);
      } catch (error) {
        console.error('Failed to fetch custom icons:', error);
      }
    };
    if (token) {
      fetchCustomIcons();
    }
  }, [token]);
  
  // Fetch style presets for AI generation
  useEffect(() => {
    const fetchStylePresets = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/cms/ai/style-presets`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStylePresets(response.data);
      } catch (error) {
        console.error('Failed to fetch style presets:', error);
      }
    };
    if (token) {
      fetchStylePresets();
    }
  }, [token]);

  // Fetch room templates
  useEffect(() => {
    const fetchRoomTemplates = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/cms/ai/room-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRoomTemplates(response.data);
      } catch (error) {
        console.error('Failed to fetch room templates:', error);
      }
    };
    if (token) {
      fetchRoomTemplates();
    }
  }, [token]);

  // Fetch prompt history
  const fetchPromptHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/cms/ai/prompt-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromptHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch prompt history:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchPromptHistory();
    }
  }, [token]);
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, pagesRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/cms/products`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/cms/pages`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/cms/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setProducts(productsRes.data);
      setPages(pagesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch CMS data:', error);
      toast.error('Failed to load CMS data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = () => {
    setSelectedItem({
      isNew: true,
      type: 'product',
      product_type: '',
      category_slug: 'shades',
      title: '',
      display_name: '',
      subtitle: '',
      description: '',
      short_description: '',
      hero_text: '',
      cta_text: 'Configure Your Shades',
      cta_link: '',
      base_price: 0,
      price_per_sqft: 0,
      images: [],
      features: [],
      specifications: [],
      faqs: [],
      seo: {
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        og_title: '',
        og_description: ''
      },
      is_active: true,
      is_featured: false,
      has_configurator: true,
      sort_order: products.length + 1
    });
    setIsModalOpen(true);
  };

  // Default content that matches what's shown on public pages
  const defaultOperatingSystems = [
    { id: 'default-1', icon: 'Zap', title: 'Motorized', description: 'Remote, app, or voice control with smart home integration. Whisper-quiet motors.', image_url: '' },
    { id: 'default-2', icon: 'RotateCcw', title: 'Continuous Loop', description: 'Smooth chain operation for precise positioning. Clean, modern look.', image_url: '' },
    { id: 'default-3', icon: 'Hand', title: 'Cordless', description: 'Child-safe operation with gentle push/pull. No cords or chains.', image_url: '' }
  ];
  
  const defaultBenefits = [
    { id: 'default-1', text: 'Clean, modern aesthetic', image_url: '' },
    { id: 'default-2', text: 'Privacy and light control', image_url: '' },
    { id: 'default-3', text: 'Wide fabric selection', image_url: '' },
    { id: 'default-4', text: 'Ideal for homes and commercial spaces', image_url: '' },
    { id: 'default-5', text: 'Smart home integration available', image_url: '' }
  ];

  const handleEditProduct = (product) => {
    // Populate defaults if arrays are empty (so users see what's currently displayed)
    const productWithDefaults = {
      ...product,
      type: 'product',
      isNew: false,
      // Keep existing data or use defaults
      operating_systems: product.operating_systems?.length > 0 
        ? product.operating_systems 
        : defaultOperatingSystems,
      benefits: product.benefits?.length > 0 
        ? product.benefits 
        : defaultBenefits,
      features: product.features || []
    };
    setSelectedItem(productWithDefaults);
    setIsModalOpen(true);
  };

  const handleEditPage = (page) => {
    setSelectedItem({ ...page, type: 'page', isNew: false });
    setIsModalOpen(true);
  };

  const handleCreateCategory = () => {
    setSelectedItem({
      isNew: true,
      type: 'category',
      name: '',
      slug: '',
      description: '',
      image_url: '',
      icon: 'Sun',
      sort_order: categories.length + 1,
      is_active: true,
      seo: {
        meta_title: '',
        meta_description: '',
        meta_keywords: ''
      }
    });
    setIsModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setSelectedItem({ ...category, type: 'category', isNew: false });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!selectedItem) return;
    setSaving(true);

    try {
      let endpoint;
      if (selectedItem.type === 'product') {
        endpoint = `/api/admin/cms/products${selectedItem.isNew ? '' : `/${selectedItem.id}`}`;
      } else if (selectedItem.type === 'category') {
        endpoint = `/api/admin/cms/categories${selectedItem.isNew ? '' : `/${selectedItem.id}`}`;
      } else {
        endpoint = `/api/admin/cms/pages/${selectedItem.id}`;
      }
      
      const method = selectedItem.isNew ? 'post' : 'put';
      
      // Clean up the data before sending
      const dataToSend = { ...selectedItem };
      delete dataToSend.type;
      delete dataToSend.isNew;

      await axios[method](`${API_URL}${endpoint}`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const typeLabel = selectedItem.type === 'product' ? 'Product' : selectedItem.type === 'category' ? 'Category' : 'Page';
      toast.success(`${typeLabel} saved successfully!`);
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      let endpoint;
      if (type === 'product') {
        endpoint = `/api/admin/cms/products/${item.id}`;
      } else if (type === 'category') {
        endpoint = `/api/admin/cms/categories/${item.id}`;
      } else {
        endpoint = `/api/admin/cms/pages/${item.id}`;
      }
      
      await axios.delete(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${type === 'product' ? 'Product' : 'Page'} deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleGenerateSEO = async () => {
    if (!selectedItem) return;
    setGeneratingSEO(true);

    try {
      const contentData = selectedItem.type === 'product' 
        ? {
            product_type: selectedItem.product_type,
            title: selectedItem.title,
            description: selectedItem.description,
            features: selectedItem.features?.slice(0, 3) || []
          }
        : {
            name: selectedItem.name,
            slug: selectedItem.slug,
            path: selectedItem.path,
            page_type: selectedItem.page_type,
            content_preview: selectedItem.sections?.map(s => s.content || '').join(' ').slice(0, 300)
          };

      const response = await axios.post(`${API_URL}/api/admin/cms/generate-seo`, {
        content_type: selectedItem.type,
        content_data: contentData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSelectedItem(prev => ({
          ...prev,
          seo: { ...prev.seo, ...response.data.seo }
        }));
        toast.success('SEO suggestions generated!');
      } else {
        toast.error(response.data.error || 'Failed to generate SEO');
      }
    } catch (error) {
      console.error('Failed to generate SEO:', error);
      toast.error('Failed to generate SEO suggestions');
    } finally {
      setGeneratingSEO(false);
    }
  };

  const handlePreview = () => {
    if (!selectedItem) return;
    
    // Get the base URL without /api
    const baseUrl = API_URL.replace('/api', '');
    
    let previewUrl;
    if (selectedItem.type === 'product') {
      // Products are at /products/{product_type}
      previewUrl = `${baseUrl}/products/${selectedItem.product_type}`;
    } else {
      // Pages use their path directly
      previewUrl = `${baseUrl}${selectedItem.path || `/${selectedItem.slug}`}`;
    }
    
    window.open(previewUrl, '_blank');
  };

  // Image management handlers
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/admin/cms/upload-media?category=products`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newImage = {
        id: response.data.media?.id || Date.now().toString(),
        url: response.data.url,
        alt_text: file.name.replace(/\.[^/.]+$/, ''),
        is_primary: !selectedItem.images?.length,
        sort_order: selectedItem.images?.length || 0
      };

      setSelectedItem(prev => ({
        ...prev,
        images: [...(prev.images || []), newImage]
      }));

      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleRemoveImage = (imageId) => {
    setSelectedItem(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  const handleSetPrimaryImage = (imageId) => {
    setSelectedItem(prev => ({
      ...prev,
      images: prev.images.map(img => ({
        ...img,
        is_primary: img.id === imageId
      }))
    }));
  };

  const handleUpdateImageAlt = (imageId, altText) => {
    setSelectedItem(prev => ({
      ...prev,
      images: prev.images.map(img => 
        img.id === imageId ? { ...img, alt_text: altText } : img
      )
    }));
  };

  const handleAddImageUrl = () => {
    const url = prompt('Enter image URL:');
    if (!url) return;

    const newImage = {
      id: Date.now().toString(),
      url: url,
      alt_text: '',
      is_primary: !selectedItem.images?.length,
      sort_order: selectedItem.images?.length || 0
    };

    setSelectedItem(prev => ({
      ...prev,
      images: [...(prev.images || []), newImage]
    }));
  };

  // AI Image Generation Handler
  const handleGenerateAIImage = async () => {
    if (!selectedItem?.id && !selectedItem?.isNew) {
      toast.error('Please save the product first before generating AI images');
      return;
    }

    setGeneratingAIImage(true);
    try {
      // Use product info for the prompt
      const productType = selectedItem.product_type || selectedItem.title || 'window treatment';
      const productName = selectedItem.title || selectedItem.display_name || productType;
      const description = selectedItem.short_description || selectedItem.description || '';
      
      const response = await axios.post(
        `${API_URL}/api/admin/cms/products/${selectedItem.id}/generate-ai-image`,
        {
          product_name: productName,
          product_type: productType,
          description: description,
          style: aiImageSettings.style,
          room_type: aiImageSettings.room_type,
          color_scheme: aiImageSettings.color_scheme
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.url) {
        const newImage = {
          id: response.data.media?.id || Date.now().toString(),
          url: response.data.url,
          alt_text: `AI generated ${productType} in ${aiImageSettings.room_type}`,
          is_primary: !selectedItem.images?.length,
          sort_order: selectedItem.images?.length || 0
        };

        setSelectedItem(prev => ({
          ...prev,
          images: [...(prev.images || []), newImage]
        }));

        toast.success('AI image generated and added!');
        setShowAIImageModal(false);
      }
    } catch (error) {
      console.error('Failed to generate AI image:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI image. Please try again.');
    } finally {
      setGeneratingAIImage(false);
    }
  };

  // Apply a style preset
  const applyStylePreset = (presetKey) => {
    const preset = stylePresets[presetKey];
    if (preset) {
      setAiImageSettings({
        room_type: preset.room_type,
        style: preset.style,
        color_scheme: preset.color_scheme
      });
      toast.success(`Applied "${preset.name}" preset`);
    }
  };

  // Batch Generation Handler
  const handleBatchGeneration = async () => {
    if (!selectedItem?.id) {
      toast.error('Please save the product first');
      return;
    }

    setGeneratingBatch(true);
    setBatchProgress({ current: 0, total: 3 });
    
    try {
      const productType = selectedItem.product_type || selectedItem.title || 'window treatment';
      const productName = selectedItem.title || selectedItem.display_name || productType;
      const description = selectedItem.short_description || selectedItem.description || '';
      
      toast.info('Generating 3 images: Front View, Detail Shot, and Lifestyle...');
      
      const response = await axios.post(
        `${API_URL}/api/admin/cms/products/${selectedItem.id}/generate-batch`,
        {
          product_name: productName,
          product_type: productType,
          description: description,
          style: aiImageSettings.style,
          room_type: aiImageSettings.room_type,
          color_scheme: aiImageSettings.color_scheme
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.images && response.data.images.length > 0) {
        const newImages = response.data.images.map((img, idx) => ({
          id: img.media?.id || Date.now().toString() + idx,
          url: img.url,
          alt_text: `AI generated ${img.name} - ${productType}`,
          is_primary: !selectedItem.images?.length && idx === 0,
          sort_order: (selectedItem.images?.length || 0) + idx
        }));

        setSelectedItem(prev => ({
          ...prev,
          images: [...(prev.images || []), ...newImages]
        }));

        toast.success(`Generated ${response.data.images.length} images successfully!`);
        setShowAIImageModal(false);
      }
    } catch (error) {
      console.error('Batch generation failed:', error);
      toast.error(error.response?.data?.detail || 'Batch generation failed. Please try again.');
    } finally {
      setGeneratingBatch(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  // Category AI Image Generation
  const handleCategoryAIGeneration = async () => {
    if (!selectedCategoryForAI?.id) {
      toast.error('No category selected');
      return;
    }

    setGeneratingAIImage(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/cms/categories/${selectedCategoryForAI.id}/generate-ai-image`,
        null,
        {
          params: {
            room_type: aiImageSettings.room_type,
            style: aiImageSettings.style,
            color_scheme: aiImageSettings.color_scheme
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.url) {
        // Update the category with the new hero image
        await axios.put(
          `${API_URL}/api/admin/cms/categories/${selectedCategoryForAI.id}`,
          { ...selectedCategoryForAI, hero_image: response.data.url },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Refresh categories
        fetchData();
        toast.success('Category hero image generated!');
        setShowCategoryAIModal(false);
        setSelectedCategoryForAI(null);
      }
    } catch (error) {
      console.error('Category AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate category image.');
    } finally {
      setGeneratingAIImage(false);
    }
  };

  // Generate Image Variation
  const handleGenerateVariation = async (mediaId, imageUrl) => {
    setGeneratingVariation(mediaId);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/cms/media/${mediaId}/generate-variation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.url) {
        const newImage = {
          id: response.data.media?.id || Date.now().toString(),
          url: response.data.url,
          alt_text: 'AI generated variation',
          is_primary: false,
          sort_order: (selectedItem.images?.length || 0)
        };

        setSelectedItem(prev => ({
          ...prev,
          images: [...(prev.images || []), newImage]
        }));

        toast.success('Variation generated and added!');
      }
    } catch (error) {
      console.error('Variation generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate variation.');
    } finally {
      setGeneratingVariation(null);
    }
  };

  // Save current settings as prompt
  const handleSavePrompt = async (name) => {
    try {
      const productType = selectedItem?.product_type || selectedItem?.title || 'window treatment';
      const response = await axios.post(
        `${API_URL}/api/admin/cms/ai/prompt-history`,
        {
          name: name,
          prompt: `${aiImageSettings.style} ${aiImageSettings.room_type} with ${productType}`,
          settings: aiImageSettings,
          product_type: productType,
          is_favorite: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPromptHistory(prev => [response.data, ...prev]);
      toast.success('Prompt saved to history!');
    } catch (error) {
      toast.error('Failed to save prompt');
    }
  };

  // Use saved prompt
  const handleUsePrompt = async (prompt) => {
    setAiImageSettings(prompt.settings);
    toast.success(`Applied "${prompt.name}" settings`);
    setShowPromptHistoryModal(false);
    
    // Track usage
    try {
      await axios.post(
        `${API_URL}/api/admin/cms/ai/prompt-history/${prompt.id}/use`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to track prompt usage:', error);
    }
  };

  // Toggle prompt favorite
  const handleToggleFavorite = async (promptId) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/cms/ai/prompt-history/${promptId}/favorite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPromptHistory(prev => prev.map(p => 
        p.id === promptId ? { ...p, is_favorite: response.data.is_favorite } : p
      ));
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  // Delete saved prompt
  const handleDeletePrompt = async (promptId) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/cms/ai/prompt-history/${promptId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPromptHistory(prev => prev.filter(p => p.id !== promptId));
      toast.success('Prompt deleted');
    } catch (error) {
      toast.error('Failed to delete prompt');
    }
  };

  // Generate auto-variations for comparison
  const handleGenerateAutoVariations = async () => {
    if (!selectedItem?.id) {
      toast.error('Please save the product first');
      return;
    }

    setGeneratingAutoVariations(true);
    try {
      const productType = selectedItem.product_type || selectedItem.title || 'window treatment';
      const productName = selectedItem.title || selectedItem.display_name || productType;
      const description = selectedItem.short_description || selectedItem.description || '';
      
      toast.info('Generating 3 variations for comparison...');
      
      const response = await axios.post(
        `${API_URL}/api/admin/cms/products/${selectedItem.id}/generate-auto-variations`,
        {
          product_name: productName,
          product_type: productType,
          description: description,
          style: aiImageSettings.style,
          room_type: aiImageSettings.room_type,
          color_scheme: aiImageSettings.color_scheme
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.variations && response.data.variations.length > 0) {
        setComparisonImages(response.data.variations);
        setShowComparisonModal(true);
        setShowAIImageModal(false);
        toast.success('Variations ready for comparison!');
      }
    } catch (error) {
      console.error('Auto-variations failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate variations');
    } finally {
      setGeneratingAutoVariations(false);
    }
  };

  // Select a variation from comparison
  const handleSelectVariation = async (variation) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/cms/ai/select-variation`,
        null,
        {
          params: { media_id: variation.media.id, product_id: selectedItem.id },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const newImage = {
        id: variation.media.id,
        url: variation.url,
        alt_text: `AI generated ${variation.name}`,
        is_primary: !selectedItem.images?.length,
        sort_order: selectedItem.images?.length || 0
      };

      setSelectedItem(prev => ({
        ...prev,
        images: [...(prev.images || []), newImage]
      }));

      setShowComparisonModal(false);
      setComparisonImages([]);
      toast.success(`Selected "${variation.name}" and added to product!`);
    } catch (error) {
      toast.error('Failed to select variation');
    }
  };

  // Toggle gallery visibility for an image
  const handleToggleGallery = async (mediaId, showInGallery, galleryData = {}) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/cms/media/${mediaId}/toggle-gallery`,
        null,
        {
          params: {
            show_in_gallery: showInGallery,
            gallery_title: galleryData.title || '',
            gallery_description: galleryData.description || '',
            gallery_category: galleryData.category || 'residential',
            gallery_product: galleryData.product || 'roller-shades'
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(showInGallery ? 'Added to Gallery!' : 'Removed from Gallery');
      return true;
    } catch (error) {
      console.error('Failed to toggle gallery visibility:', error);
      toast.error('Failed to update gallery visibility');
      return false;
    }
  };


  // Generate from template
  const handleGenerateFromTemplate = async (template) => {
    if (!selectedItem?.id) {
      toast.error('Please save the product first');
      return;
    }

    setGeneratingAIImage(true);
    try {
      const productType = selectedItem.product_type || selectedItem.title || 'window treatment';
      const productName = selectedItem.title || selectedItem.display_name || productType;
      
      const response = await axios.post(
        `${API_URL}/api/admin/cms/ai/generate-from-template`,
        null,
        {
          params: {
            template_id: template.id,
            product_type: productType,
            product_name: productName,
            color_scheme: aiImageSettings.color_scheme
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.url) {
        const newImage = {
          id: response.data.media?.id || Date.now().toString(),
          url: response.data.url,
          alt_text: `${template.name} - ${productType}`,
          is_primary: !selectedItem.images?.length,
          sort_order: selectedItem.images?.length || 0
        };

        setSelectedItem(prev => ({
          ...prev,
          images: [...(prev.images || []), newImage]
        }));

        setShowTemplatesModal(false);
        toast.success(`Generated image using "${template.name}" template!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate from template');
    } finally {
      setGeneratingAIImage(false);
    }
  };

  // Generate AI image for category (single image)
  const handleGenerateCategoryAIImage = async () => {
    if (!selectedItem?.id || selectedItem.type !== 'category') {
      toast.error('Please save the category first');
      return;
    }

    setGeneratingCategoryAI(true);
    try {
      // Build color scheme from brand colors if enabled
      const colorScheme = brandColors.enabled 
        ? `${brandColors.primary}, ${brandColors.secondary}, ${brandColors.accent}`
        : aiImageSettings.color_scheme;

      const response = await axios.post(
        `${API_URL}/api/admin/cms/categories/${selectedItem.id}/generate-ai-image`,
        null,
        {
          params: {
            room_type: aiImageSettings.room_type,
            style: aiImageSettings.style,
            color_scheme: colorScheme
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.url) {
        setSelectedItem(prev => ({ ...prev, image_url: response.data.url }));
        toast.success('AI image generated for category!');
        setShowCategoryAIFullModal(false);
      }
    } catch (error) {
      console.error('Category AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setGeneratingCategoryAI(false);
    }
  };

  // Generate images for ALL categories at once
  const [generatingAllCategories, setGeneratingAllCategories] = useState(false);
  const [bulkCategoryProgress, setBulkCategoryProgress] = useState({ current: 0, total: 0 });
  
  const handleGenerateAllCategoryImages = async () => {
    if (categories.length === 0) {
      toast.error('No categories to generate images for');
      return;
    }

    const confirmed = window.confirm(
      `This will generate hero images for all ${categories.length} categories. This may take a few minutes. Continue?`
    );
    if (!confirmed) return;

    setGeneratingAllCategories(true);
    setBulkCategoryProgress({ current: 0, total: categories.length });
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/cms/categories/generate-all-images`,
        null,
        {
          params: {
            room_type: aiImageSettings.room_type || 'living room',
            style: aiImageSettings.style || 'modern luxury'
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000 // 5 minute timeout for bulk operation
        }
      );

      const { generated, failed } = response.data;
      
      if (generated && generated.length > 0) {
        toast.success(`Generated ${generated.length} category images!`);
        fetchData(); // Refresh categories to show new images
      }
      
      if (failed && failed.length > 0) {
        toast.error(`${failed.length} categories failed to generate`);
      }
    } catch (error) {
      console.error('Bulk category image generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate bulk images');
    } finally {
      setGeneratingAllCategories(false);
      setBulkCategoryProgress({ current: 0, total: 0 });
    }
  };



  // Generate 3 auto-variations for category
  const handleGenerateCategoryAutoVariations = async () => {
    if (!selectedItem?.id || selectedItem.type !== 'category') {
      toast.error('Please save the category first');
      return;
    }

    setGeneratingCategoryAutoVar(true);
    try {
      const colorScheme = brandColors.enabled 
        ? `${brandColors.primary}, ${brandColors.secondary}, ${brandColors.accent}`
        : aiImageSettings.color_scheme;

      toast.info('Generating 3 variations for comparison...');
      
      const response = await axios.post(
        `${API_URL}/api/admin/cms/categories/${selectedItem.id}/generate-auto-variations`,
        {
          room_type: aiImageSettings.room_type,
          style: aiImageSettings.style,
          color_scheme: colorScheme
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.variations && response.data.variations.length > 0) {
        setCategoryComparisonImages(response.data.variations);
        setShowCategoryComparisonModal(true);
        setShowCategoryAIFullModal(false);
        toast.success('Variations ready for comparison!');
      }
    } catch (error) {
      console.error('Category auto-variations failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate variations');
    } finally {
      setGeneratingCategoryAutoVar(false);
    }
  };

  // Select a category variation from comparison
  const handleSelectCategoryVariation = async (variation) => {
    setSelectedItem(prev => ({ ...prev, image_url: variation.url }));
    setShowCategoryComparisonModal(false);
    setCategoryComparisonImages([]);
    toast.success(`Selected "${variation.name}" for category!`);
  };

  // Open image preview modal
  const openImagePreview = (imageUrl, altText = 'Preview', mediaId = null) => {
    setPreviewImage({ url: imageUrl, alt: altText, media_id: mediaId });
    setShowImagePreviewModal(true);
  };

  const filteredProducts = products.filter(p => 
    p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.product_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category_slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPages = pages.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCategories = categories.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-tab">
      {/* Sub-navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setActiveView('categories')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeView === 'categories' ? 'bg-amber-100 text-amber-800' : 'hover:bg-neutral-100'
            }`}
            data-testid="cms-categories-tab"
          >
            <FolderTree className="w-4 h-4" /> Categories ({categories.length})
          </button>
          <button
            onClick={() => setActiveView('products')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeView === 'products' ? 'bg-amber-100 text-amber-800' : 'hover:bg-neutral-100'
            }`}
            data-testid="cms-products-tab"
          >
            <Package className="w-4 h-4" /> Products ({products.length})
          </button>
          <button
            onClick={() => setActiveView('pages')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeView === 'pages' ? 'bg-amber-100 text-amber-800' : 'hover:bg-neutral-100'
            }`}
            data-testid="cms-pages-tab"
          >
            <FileText className="w-4 h-4" /> Pages ({pages.length})
          </button>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
              data-testid="cms-search"
            />
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {activeView === 'categories' && (
            <>
              <Button 
                onClick={handleGenerateAllCategoryImages}
                disabled={generatingAllCategories || categories.length === 0}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                data-testid="bulk-category-images-btn"
              >
                {generatingAllCategories ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    AI Generate All
                  </>
                )}
              </Button>
              <Button onClick={handleCreateCategory} data-testid="cms-add-category">
                <Plus className="w-4 h-4 mr-2" /> Add Category
              </Button>
            </>
          )}
          {activeView === 'products' && (
            <Button onClick={handleCreateProduct} data-testid="cms-add-product">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          )}
        </div>
      </div>

      {/* Categories View */}
      {activeView === 'categories' && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Category</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Slug</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Products</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Status</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredCategories.map((category) => {
                const productCount = products.filter(p => p.category_slug === category.slug).length;
                return (
                  <tr key={category.id} className="hover:bg-neutral-50" data-testid={`category-row-${category.slug}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {category.image_url ? (
                          <img 
                            src={category.image_url} 
                            alt={category.name}
                            className="w-12 h-12 rounded object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded bg-amber-50 flex items-center justify-center">
                            <Layers className="w-5 h-5 text-amber-600" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium">{category.name}</div>
                          <div className="text-sm text-neutral-500">{category.description?.slice(0, 50)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-neutral-100 rounded text-sm">{category.slug}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm">{productCount} products</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        category.is_active ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-600'
                      }`}>
                        {category.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedCategoryForAI(category);
                            setShowCategoryAIModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          title="Generate AI hero image"
                          data-testid={`ai-generate-category-${category.slug}`}
                        >
                          <Sparkles className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditCategory(category)}
                          data-testid={`edit-category-${category.slug}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(category, 'category')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredCategories.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-neutral-500">
                    No categories found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Products View */}
      {activeView === 'products' && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Product</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Category</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Price</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">SEO</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Status</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-neutral-50" data-testid={`product-row-${product.product_type}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {product.images?.[0]?.url ? (
                        <img 
                          src={product.images[0].url} 
                          alt={product.title}
                          className="w-12 h-12 rounded object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded bg-neutral-100 flex items-center justify-center">
                          <Image className="w-5 h-5 text-neutral-400" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{product.display_name || product.title}</div>
                        <div className="text-sm text-neutral-500">{product.short_description?.slice(0, 40)}...</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      product.category_slug === 'shades' ? 'bg-blue-100 text-blue-700' :
                      product.category_slug === 'drapery' ? 'bg-purple-100 text-purple-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {product.category_slug || 'uncategorized'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-neutral-100 rounded text-sm">{product.product_type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div>Base: ${product.base_price}</div>
                      <div className="text-neutral-500">${product.price_per_sqft}/sqft</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {product.seo?.meta_title ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <Check className="w-4 h-4" /> Configured
                      </span>
                    ) : (
                      <span className="text-amber-600 text-sm">Missing</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      product.is_active ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-600'
                    }`}>
                      {product.is_active ? 'Active' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditProduct(product)}
                        data-testid={`edit-product-${product.product_type}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(product, 'product')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-neutral-500">
                    No products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pages View */}
      {activeView === 'pages' && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Page</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Path</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">SEO</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Status</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredPages.map((page) => (
                <tr key={page.id} className="hover:bg-neutral-50" data-testid={`page-row-${page.slug}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium">{page.name}</div>
                        <div className="text-sm text-neutral-500">/{page.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-600">{page.path}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      page.is_system ? 'bg-purple-100 text-purple-700' : 'bg-neutral-100 text-neutral-600'
                    }`}>
                      {page.page_type || 'static'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {page.seo?.meta_title ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <Check className="w-4 h-4" /> Configured
                      </span>
                    ) : (
                      <span className="text-amber-600 text-sm">Missing</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      page.is_active ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-600'
                    }`}>
                      {page.is_active ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditPage(page)}
                        data-testid={`edit-page-${page.slug}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {!page.is_system && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(page, 'page')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredPages.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-neutral-500">
                    No pages found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedItem?.type === 'product' ? (
                <><Package className="w-5 h-5" /> {selectedItem?.isNew ? 'New Product' : `Edit: ${selectedItem?.title}`}</>
              ) : (
                <><FileText className="w-5 h-5" /> Edit: {selectedItem?.name}</>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-6 mt-4">
              {/* Product Fields */}
              {selectedItem.type === 'product' && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Product Type (slug)</Label>
                      <Input
                        value={selectedItem.product_type || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, product_type: e.target.value }))}
                        placeholder="e.g., roller-shades"
                        className="mt-1"
                        disabled={!selectedItem.isNew}
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <select
                        value={selectedItem.category_slug || 'shades'}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, category_slug: e.target.value }))}
                        className="mt-1 w-full rounded-md border border-neutral-200 px-3 py-2 text-sm"
                      >
                        {categories.map(cat => (
                          <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={selectedItem.title || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g., Roller Shades"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <FieldLabel 
                        label="Display Name" 
                        tooltip="The main product title shown to customers on product cards and the hero section of the product detail page."
                        helpText="Product cards & hero title"
                      />
                      <Input
                        value={selectedItem.display_name || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, display_name: e.target.value }))}
                        placeholder="e.g., Custom Roller Shades"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <FieldLabel 
                        label="Subtitle" 
                        tooltip="An optional tagline or secondary text. Currently stored but not displayed on the frontend."
                        helpText="Not displayed (future use)"
                      />
                      <Input
                        value={selectedItem.subtitle || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, subtitle: e.target.value }))}
                        placeholder="e.g., Classic Elegance, Modern Function"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <FieldLabel 
                      label="Hero Text" 
                      tooltip="The main descriptive text displayed in the hero banner on the product detail page. This is the primary text customers see when viewing the product."
                      helpText="Hero banner on product detail page"
                    />
                    <Textarea
                      value={selectedItem.hero_text || ''}
                      onChange={(e) => setSelectedItem(prev => ({ ...prev, hero_text: e.target.value }))}
                      placeholder="Clean, modern shades custom-made to fit your space...&#10;&#10;Press Enter for new lines"
                      className="mt-1 min-h-24"
                      rows={3}
                    />
                    <p className="text-xs text-neutral-500 mt-1">Press Enter for line breaks</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <FieldLabel 
                        label="CTA Button Text" 
                        tooltip="The text displayed on the call-to-action button in the hero section (e.g., 'Configure Your Shades', 'Shop Now')."
                        helpText="Button in hero section"
                      />
                      <Input
                        value={selectedItem.cta_text || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, cta_text: e.target.value }))}
                        placeholder="e.g., Configure Your Shades"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <FieldLabel 
                        label="CTA Link" 
                        tooltip="The URL the CTA button links to. Usually the product configurator page."
                        helpText="Button destination URL"
                      />
                      <Input
                        value={selectedItem.cta_link || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, cta_link: e.target.value }))}
                        placeholder="e.g., /configure/roller-shades"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <FieldLabel 
                      label="Description" 
                      tooltip="Rich text description of the product. Used as fallback if Hero Text is empty. Supports bold, italic, lists, and links."
                      helpText="Fallback for Hero Text"
                    />
                    <RichTextEditor
                      value={selectedItem.description || ''}
                      onChange={(html) => setSelectedItem(prev => ({ ...prev, description: html }))}
                      placeholder="Full product description..."
                      className="mt-1"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Use toolbar for formatting: bold, italic, lists, links</p>
                  </div>

                  <div>
                    <FieldLabel 
                      label="Short Description" 
                      tooltip="A brief summary shown on product cards in the /products listing page. Keep it concise (1-2 sentences)."
                      helpText="Product cards on /products page"
                    />
                    <Textarea
                      value={selectedItem.short_description || ''}
                      onChange={(e) => setSelectedItem(prev => ({ ...prev, short_description: e.target.value }))}
                      placeholder="Brief description for product listings"
                      className="mt-1 min-h-16"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Base Price ($)</Label>
                      <Input
                        type="number"
                        value={selectedItem.base_price || 0}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Price per Sq Ft ($)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={selectedItem.price_per_sqft || 0}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, price_per_sqft: parseFloat(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedItem.is_active}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">Active (visible on site)</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedItem.is_featured}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, is_featured: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">Featured Product</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedItem.has_configurator}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, has_configurator: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">Has Configurator</span>
                    </label>
                  </div>

                  {/* Product Images Section */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Image className="w-5 h-5" /> Product Images
                      </h3>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAIImageModal(true)}
                          disabled={!selectedItem.id || generatingAIImage}
                          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 hover:from-purple-600 hover:to-pink-600"
                          data-testid="generate-ai-image-btn"
                        >
                          {generatingAIImage ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-1" />
                          )}
                          AI Generate
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openImageGallery((url) => {
                            const newImage = { id: Date.now().toString(), url, is_primary: !selectedItem.images?.length };
                            setSelectedItem(prev => ({ ...prev, images: [...(prev.images || []), newImage] }));
                          }, 'products')}
                        >
                          <FolderOpen className="w-4 h-4 mr-1" /> Gallery
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddImageUrl}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add URL
                        </Button>
                        <label className="cursor-pointer">
                          <Button
                            variant="default"
                            size="sm"
                            disabled={uploadingImage}
                            asChild
                          >
                            <span>
                              {uploadingImage ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4 mr-1" />
                              )}
                              Upload Image
                            </span>
                          </Button>
                          <input
                            type="file"
                            accept="image/*,video/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            data-testid="image-upload-input"
                          />
                        </label>
                      </div>
                    </div>

                    {/* Images Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {(selectedItem.images || []).map((image, index) => {
                        const isVideo = image.url?.match(/\.(mp4|webm|mov|avi|wmv)$/i) || image.media_type === 'video';
                        return (
                        <div 
                          key={image.id} 
                          className={`relative group border rounded-lg overflow-hidden ${
                            image.is_primary ? 'ring-2 ring-amber-500' : ''
                          }`}
                          data-testid={`product-image-${index}`}
                        >
                          {isVideo ? (
                            <div className="relative w-full h-32 bg-neutral-900">
                              <video
                                src={image.url.startsWith('/') ? `${API_URL}${image.url}` : image.url}
                                className="w-full h-full object-cover"
                                muted
                              />
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
                                  <Play className="w-5 h-5 text-neutral-700 ml-0.5" />
                                </div>
                              </div>
                              <div className="absolute top-2 right-2 bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded">
                                Video
                              </div>
                            </div>
                          ) : (
                            <img
                              src={image.url.startsWith('/') ? `${API_URL}${image.url}` : image.url}
                              alt={image.alt_text || 'Product image'}
                              className="w-full h-32 object-cover cursor-pointer"
                              onClick={() => openImagePreview(
                                image.url.startsWith('/') ? `${API_URL}${image.url}` : image.url,
                                image.alt_text || 'Product image',
                                image.id
                              )}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/200x200?text=Image+Error';
                              }}
                            />
                          )}
                          
                          {/* Primary badge */}
                          {image.is_primary && (
                            <div className="absolute top-2 left-2 bg-amber-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                              <Star className="w-3 h-3 fill-current" /> Primary
                            </div>
                          )}

                          {/* Hover actions */}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 flex-wrap p-2">
                            {/* Enlarge/Preview button */}
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => openImagePreview(
                                image.url.startsWith('/') ? `${API_URL}${image.url}` : image.url,
                                image.alt_text || 'Product image',
                                image.id
                              )}
                              title="View full size"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {!image.is_primary && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleSetPrimaryImage(image.id)}
                                title="Set as primary"
                              >
                                <Star className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Generate Similar - for images with a media ID that might be AI-generated */}
                            {image.id && image.id.length > 10 && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleGenerateVariation(image.id, image.url)}
                                disabled={generatingVariation === image.id}
                                title="Generate similar variation"
                                className="bg-purple-500 hover:bg-purple-600 text-white"
                              >
                                {generatingVariation === image.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRemoveImage(image.id)}
                              title="Remove image"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Alt text input */}
                          <div className="p-2 bg-neutral-50">
                            <Input
                              value={image.alt_text || ''}
                              onChange={(e) => handleUpdateImageAlt(image.id, e.target.value)}
                              placeholder="Alt text..."
                              className="text-xs h-7"
                            />
                          </div>
                        </div>
                        );
                      })}

                      {/* Empty state */}
                      {(!selectedItem.images || selectedItem.images.length === 0) && (
                        <div className="col-span-full border-2 border-dashed rounded-lg p-8 text-center text-neutral-500">
                          <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No images yet</p>
                          <p className="text-xs mt-1">Upload images or add URLs to showcase this product</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ========== SECTION 1: FEATURES (Collapsible) ========== */}
                  <div className="border-t pt-4">
                    <button
                      onClick={() => toggleSection('features')}
                      className="w-full flex items-center justify-between p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Star className="w-5 h-5 text-amber-700" />
                        <span className="font-semibold text-amber-900">1. Features Section</span>
                        <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                          {(selectedItem.features || []).length} items
                        </span>
                      </div>
                      {expandedSections.features ? <ChevronDown className="w-5 h-5 text-amber-700" /> : <ChevronRight className="w-5 h-5 text-amber-700" />}
                    </button>
                    
                    {expandedSections.features && (
                      <div className="mt-3 space-y-3 pl-2">
                        {/* Section Title */}
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-neutral-600 w-28">Section Title:</Label>
                          <Input
                            value={selectedItem.features_section_title || 'What Makes It Special'}
                            onChange={(e) => setSelectedItem(prev => ({ ...prev, features_section_title: e.target.value }))}
                            placeholder="What Makes It Special"
                            className="flex-1"
                          />
                        </div>
                        
                        {/* Features List - Drag and Drop */}
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'features')}>
                          <SortableContext items={(selectedItem.features || []).map(f => f.id)} strategy={verticalListSortingStrategy}>
                            <div className="space-y-3">
                              {(selectedItem.features || []).map((feature, idx) => (
                                <SortableItem key={feature.id} id={feature.id}>
                                  <div className="p-4 bg-neutral-50 rounded-lg border">
                                    <div className="flex gap-4">
                                      {/* Image */}
                                      <div className="w-20 flex-shrink-0">
                                        {feature.image_url ? (
                                          <div className="relative">
                                            <img 
                                              src={feature.image_url.startsWith('/') ? `${API_URL}${feature.image_url}` : feature.image_url}
                                              alt=""
                                              className="w-20 h-20 object-cover rounded"
                                            />
                                            <Button
                                              variant="destructive"
                                              size="sm"
                                              className="absolute -top-2 -right-2 h-6 w-6 p-0"
                                              onClick={() => {
                                                const updated = [...(selectedItem.features || [])];
                                                updated[idx] = { ...updated[idx], image_url: '' };
                                                setSelectedItem(prev => ({ ...prev, features: updated }));
                                              }}
                                            >
                                              <X className="w-3 h-3" />
                                            </Button>
                                          </div>
                                        ) : (
                                          <div className="flex flex-col gap-1">
                                            <button
                                              type="button"
                                              onClick={() => openImageGallery((url) => {
                                                const updated = [...(selectedItem.features || [])];
                                                updated[idx] = { ...updated[idx], image_url: url };
                                                setSelectedItem(prev => ({ ...prev, features: updated }));
                                              }, 'features')}
                                              className="w-20 h-14 border-2 border-dashed rounded flex flex-col items-center justify-center hover:bg-neutral-100 text-neutral-400"
                                            >
                                              <FolderOpen className="w-4 h-4" />
                                              <span className="text-xs">Gallery</span>
                                            </button>
                                            <label className="w-20 h-6 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-neutral-100 text-neutral-400">
                                              <Upload className="w-3 h-3 mr-1" />
                                              <span className="text-xs">Upload</span>
                                              <input type="file" accept="image/*,video/*" className="hidden" onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                const formData = new FormData();
                                                formData.append('file', file);
                                                try {
                                                  const response = await axios.post(`${API_URL}/api/admin/cms/upload-media?category=features`, formData, {
                                                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                                  });
                                                  const updated = [...(selectedItem.features || [])];
                                                  updated[idx] = { ...updated[idx], image_url: response.data.url };
                                                  setSelectedItem(prev => ({ ...prev, features: updated }));
                                                } catch (error) { toast.error('Failed to upload'); }
                                                e.target.value = '';
                                              }} />
                                            </label>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Fields */}
                                      <div className="flex-1 space-y-2">
                                        <div className="flex gap-2 items-center">
                                          {/* Icon Picker */}
                                          <div className="relative">
                                            <button
                                              type="button"
                                              onClick={() => setActiveIconPicker(activeIconPicker?.type === 'feature' && activeIconPicker?.index === idx ? null : { type: 'feature', index: idx })}
                                              className="flex items-center gap-2 px-3 py-2 border rounded-md bg-white hover:bg-neutral-50 min-w-[140px]"
                                            >
                                              <RenderIcon iconName={feature.icon || 'Check'} className="w-4 h-4" customIcons={customIconsCache} />
                                              <span className="text-sm text-neutral-600 truncate">
                                                {feature.icon?.startsWith('custom:') ? 'Custom' : (feature.icon || 'Check')}
                                              </span>
                                              <ChevronDown className="w-3 h-3 ml-auto text-neutral-400" />
                                            </button>
                                            {activeIconPicker?.type === 'feature' && activeIconPicker?.index === idx && (
                                              <IconPicker
                                                value={feature.icon}
                                                onChange={(iconName) => {
                                                  const updated = [...(selectedItem.features || [])];
                                                  updated[idx] = { ...updated[idx], icon: iconName };
                                                  setSelectedItem(prev => ({ ...prev, features: updated }));
                                                  if (iconName.startsWith('custom:')) {
                                                    axios.get(`${API_URL}/api/admin/cms/icons`, {
                                                      headers: { Authorization: `Bearer ${token}` }
                                                    }).then(res => setCustomIconsCache(res.data)).catch(() => {});
                                                  }
                                                }}
                                                onClose={() => setActiveIconPicker(null)}
                                                token={token}
                                              />
                                            )}
                                          </div>
                                          <Input
                                            value={feature.title || ''}
                                            onChange={(e) => {
                                              const updated = [...(selectedItem.features || [])];
                                              updated[idx] = { ...updated[idx], title: e.target.value };
                                              setSelectedItem(prev => ({ ...prev, features: updated }));
                                            }}
                                            placeholder="Feature Title"
                                            className="flex-1"
                                          />
                                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => {
                                            setSelectedItem(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));
                                          }}>
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                        <Textarea
                                          value={feature.description || ''}
                                          onChange={(e) => {
                                            const updated = [...(selectedItem.features || [])];
                                            updated[idx] = { ...updated[idx], description: e.target.value };
                                            setSelectedItem(prev => ({ ...prev, features: updated }));
                                          }}
                                          placeholder="Feature description..."
                                          className="min-h-16"
                                          rows={2}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </SortableItem>
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                        <p className="text-xs text-neutral-500 mt-1">Drag items by the handle on the left to reorder</p>
                        
                        {/* Add Feature Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-dashed"
                          onClick={() => {
                            setSelectedItem(prev => ({
                              ...prev,
                              features: [...(prev.features || []), { id: Date.now().toString(), icon: 'Check', title: '', description: '', image_url: '' }]
                            }));
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Feature
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* ========== SECTION 2: OPERATING SYSTEMS (Collapsible) ========== */}
                  <div className="border-t pt-4">
                    <button
                      onClick={() => toggleSection('operatingSystems')}
                      className="w-full flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Zap className="w-5 h-5 text-blue-700" />
                        <span className="font-semibold text-blue-900">2. Operating Systems Section</span>
                        <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                          {(selectedItem.operating_systems || []).length} items
                        </span>
                      </div>
                      {expandedSections.operatingSystems ? <ChevronDown className="w-5 h-5 text-blue-700" /> : <ChevronRight className="w-5 h-5 text-blue-700" />}
                    </button>
                    
                    {expandedSections.operatingSystems && (
                      <div className="mt-3 space-y-3 pl-2">
                        {/* Section Title */}
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-neutral-600 w-28">Section Title:</Label>
                          <Input
                            value={selectedItem.operating_systems_title || 'Choose Your Control Type'}
                            onChange={(e) => setSelectedItem(prev => ({ ...prev, operating_systems_title: e.target.value }))}
                            placeholder="Choose Your Control Type"
                            className="flex-1"
                          />
                        </div>
                        
                        {/* Operating Systems List */}
                        <div className="space-y-3">
                          {(selectedItem.operating_systems || []).map((system, idx) => (
                            <div key={system.id} className="p-4 bg-neutral-50 rounded-lg border">
                              <div className="flex gap-4">
                                {/* Image */}
                                <div className="w-20 flex-shrink-0">
                                  {system.image_url ? (
                                    <div className="relative">
                                      <img 
                                        src={system.image_url.startsWith('/') ? `${API_URL}${system.image_url}` : system.image_url}
                                        alt=""
                                        className="w-20 h-20 object-cover rounded"
                                      />
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        className="absolute -top-2 -right-2 h-6 w-6 p-0"
                                        onClick={() => {
                                          const updated = [...(selectedItem.operating_systems || [])];
                                          updated[idx] = { ...updated[idx], image_url: '' };
                                          setSelectedItem(prev => ({ ...prev, operating_systems: updated }));
                                        }}
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col gap-1">
                                      <button
                                        type="button"
                                        onClick={() => openImageGallery((url) => {
                                          const updated = [...(selectedItem.operating_systems || [])];
                                          updated[idx] = { ...updated[idx], image_url: url };
                                          setSelectedItem(prev => ({ ...prev, operating_systems: updated }));
                                        }, 'systems')}
                                        className="w-20 h-14 border-2 border-dashed rounded flex flex-col items-center justify-center hover:bg-neutral-100 text-neutral-400"
                                      >
                                        <FolderOpen className="w-4 h-4" />
                                        <span className="text-xs">Gallery</span>
                                      </button>
                                      <label className="w-20 h-6 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-neutral-100 text-neutral-400">
                                        <Upload className="w-3 h-3 mr-1" />
                                        <span className="text-xs">Upload</span>
                                        <input type="file" accept="image/*,video/*" className="hidden" onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          const formData = new FormData();
                                          formData.append('file', file);
                                          try {
                                            const response = await axios.post(`${API_URL}/api/admin/cms/upload-media?category=systems`, formData, {
                                              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                            });
                                            const updated = [...(selectedItem.operating_systems || [])];
                                            updated[idx] = { ...updated[idx], image_url: response.data.url };
                                            setSelectedItem(prev => ({ ...prev, operating_systems: updated }));
                                          } catch (error) { toast.error('Failed to upload'); }
                                          e.target.value = '';
                                        }} />
                                      </label>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Fields */}
                                <div className="flex-1 space-y-2">
                                  <div className="flex gap-2 items-center">
                                    {/* Icon Picker */}
                                    <div className="relative">
                                      <button
                                        type="button"
                                        onClick={() => setActiveIconPicker(activeIconPicker?.type === 'os' && activeIconPicker?.index === idx ? null : { type: 'os', index: idx })}
                                        className="flex items-center gap-2 px-3 py-2 border rounded-md bg-white hover:bg-neutral-50 min-w-[140px]"
                                      >
                                        <RenderIcon iconName={system.icon || 'Zap'} className="w-4 h-4" customIcons={customIconsCache} />
                                        <span className="text-sm text-neutral-600 truncate">
                                          {system.icon?.startsWith('custom:') ? 'Custom' : (system.icon || 'Zap')}
                                        </span>
                                        <ChevronDown className="w-3 h-3 ml-auto text-neutral-400" />
                                      </button>
                                      {activeIconPicker?.type === 'os' && activeIconPicker?.index === idx && (
                                        <IconPicker
                                          value={system.icon}
                                          onChange={(iconName) => {
                                            const updated = [...(selectedItem.operating_systems || [])];
                                            updated[idx] = { ...updated[idx], icon: iconName };
                                            setSelectedItem(prev => ({ ...prev, operating_systems: updated }));
                                            // Update cache if it's a new custom icon
                                            if (iconName.startsWith('custom:')) {
                                              axios.get(`${API_URL}/api/admin/cms/icons`, {
                                                headers: { Authorization: `Bearer ${token}` }
                                              }).then(res => setCustomIconsCache(res.data)).catch(() => {});
                                            }
                                          }}
                                          onClose={() => setActiveIconPicker(null)}
                                          token={token}
                                        />
                                      )}
                                    </div>
                                    <Input
                                      value={system.title || ''}
                                      onChange={(e) => {
                                        const updated = [...(selectedItem.operating_systems || [])];
                                        updated[idx] = { ...updated[idx], title: e.target.value };
                                        setSelectedItem(prev => ({ ...prev, operating_systems: updated }));
                                      }}
                                      placeholder="System Title (e.g., Motorized)"
                                      className="flex-1"
                                    />
                                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => {
                                      setSelectedItem(prev => ({ ...prev, operating_systems: prev.operating_systems.filter((_, i) => i !== idx) }));
                                    }}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                  <Textarea
                                    value={system.description || ''}
                                    onChange={(e) => {
                                      const updated = [...(selectedItem.operating_systems || [])];
                                      updated[idx] = { ...updated[idx], description: e.target.value };
                                      setSelectedItem(prev => ({ ...prev, operating_systems: updated }));
                                    }}
                                    placeholder="System description..."
                                    className="min-h-16"
                                    rows={2}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Add System Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-dashed"
                          onClick={() => {
                            setSelectedItem(prev => ({
                              ...prev,
                              operating_systems: [...(prev.operating_systems || []), { id: Date.now().toString(), icon: 'Zap', title: '', description: '', image_url: '' }]
                            }));
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Operating System
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* ========== SECTION 3: BENEFITS (Collapsible) ========== */}
                  <div className="border-t pt-4">
                    <button
                      onClick={() => toggleSection('benefits')}
                      className="w-full flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-green-700" />
                        <span className="font-semibold text-green-900">3. Benefits Section</span>
                        <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                          {(selectedItem.benefits || []).length} items
                        </span>
                      </div>
                      {expandedSections.benefits ? <ChevronDown className="w-5 h-5 text-green-700" /> : <ChevronRight className="w-5 h-5 text-green-700" />}
                    </button>
                    
                    {expandedSections.benefits && (
                      <div className="mt-3 space-y-3 pl-2">
                        {/* Section Title */}
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-neutral-600 w-28">Section Title:</Label>
                          <Input
                            value={selectedItem.benefits_section_title || 'Why Choose This Product?'}
                            onChange={(e) => setSelectedItem(prev => ({ ...prev, benefits_section_title: e.target.value }))}
                            placeholder="Why Choose This Product?"
                            className="flex-1"
                          />
                        </div>
                        
                        {/* Two Column Layout - Like Live Page */}
                        <div className="grid grid-cols-2 gap-6 p-4 bg-neutral-50 rounded-lg border">
                          {/* LEFT: Benefits List with Drag-and-Drop */}
                          <div>
                            <Label className="text-sm text-neutral-600 mb-3 block font-medium">Benefits (Left Side)</Label>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'benefits')}>
                              <SortableContext items={(selectedItem.benefits || []).map(b => b.id)} strategy={verticalListSortingStrategy}>
                                <div className="space-y-2">
                                  {(selectedItem.benefits || []).map((benefit, idx) => (
                                    <SortableItem key={benefit.id} id={benefit.id}>
                                      <div className="flex gap-2 items-center p-2 bg-white rounded border">
                                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                        <Input
                                          value={benefit.text || ''}
                                          onChange={(e) => {
                                            const updated = [...(selectedItem.benefits || [])];
                                            updated[idx] = { ...updated[idx], text: e.target.value };
                                            setSelectedItem(prev => ({ ...prev, benefits: updated }));
                                          }}
                                          placeholder="Benefit text..."
                                          className="flex-1 h-9"
                                        />
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => {
                                          setSelectedItem(prev => ({ ...prev, benefits: prev.benefits.filter((_, i) => i !== idx) }));
                                        }}>
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </SortableItem>
                                  ))}
                                </div>
                              </SortableContext>
                            </DndContext>
                            {(!selectedItem.benefits || selectedItem.benefits.length === 0) && (
                              <p className="text-sm text-neutral-400 text-center py-4">No benefits added yet</p>
                            )}
                            <p className="text-xs text-neutral-500 mt-1">Drag to reorder</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full mt-3 border-dashed"
                              onClick={() => {
                                setSelectedItem(prev => ({
                                  ...prev,
                                  benefits: [...(prev.benefits || []), { id: Date.now().toString(), text: '', image_url: '' }]
                                }));
                              }}
                            >
                              <Plus className="w-4 h-4 mr-1" /> Add Benefit
                            </Button>
                          </div>
                          
                          {/* RIGHT: Section Images */}
                          <div>
                            <Label className="text-sm text-neutral-600 mb-3 block font-medium">Image (Right Side)</Label>
                            <div className="flex flex-wrap gap-2">
                              {/* Existing images */}
                              {(selectedItem.benefits_images || (selectedItem.benefits_image_url ? [{ id: 'primary', url: selectedItem.benefits_image_url, is_primary: true }] : [])).map((img, idx) => (
                                <div key={img.id || idx} className={`relative group ${img.is_primary ? 'ring-2 ring-green-500 rounded' : ''}`}>
                                  <img 
                                    src={img.url?.startsWith('/') ? `${API_URL}${img.url}` : img.url}
                                    alt=""
                                    className="w-28 h-28 object-cover rounded"
                                  />
                                  {img.is_primary && (
                                    <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-1 rounded">Primary</div>
                                  )}
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded">
                                    {!img.is_primary && (
                                      <Button size="sm" variant="secondary" className="h-7 w-7 p-0" onClick={() => {
                                        const images = selectedItem.benefits_images || [{ id: 'primary', url: selectedItem.benefits_image_url, is_primary: true }];
                                        const updated = images.map(i => ({ ...i, is_primary: i.id === img.id || i.url === img.url }));
                                        setSelectedItem(prev => ({ ...prev, benefits_images: updated, benefits_image_url: img.url }));
                                      }}>
                                        <Star className="w-3 h-3" />
                                      </Button>
                                    )}
                                    <Button size="sm" variant="destructive" className="h-7 w-7 p-0" onClick={() => {
                                      const images = selectedItem.benefits_images || [{ id: 'primary', url: selectedItem.benefits_image_url, is_primary: true }];
                                      const updated = images.filter(i => (i.id !== img.id && i.url !== img.url));
                                      const primaryUrl = updated.find(i => i.is_primary)?.url || updated[0]?.url || '';
                                      setSelectedItem(prev => ({ ...prev, benefits_images: updated, benefits_image_url: primaryUrl }));
                                    }}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                              
                              {/* Add more images - Gallery + Upload */}
                              <div className="flex flex-col gap-1">
                                <button
                                  type="button"
                                  onClick={() => openImageGallery((url) => {
                                    const existingImages = selectedItem.benefits_images || (selectedItem.benefits_image_url ? [{ id: 'primary', url: selectedItem.benefits_image_url, is_primary: true }] : []);
                                    const newImage = { id: Date.now().toString(), url, is_primary: existingImages.length === 0 };
                                    setSelectedItem(prev => ({ 
                                      ...prev, 
                                      benefits_images: [...existingImages, newImage],
                                      benefits_image_url: newImage.is_primary ? url : prev.benefits_image_url
                                    }));
                                  }, 'benefits')}
                                  className="w-28 h-20 border-2 border-dashed rounded flex flex-col items-center justify-center hover:bg-white text-neutral-400"
                                >
                                  <FolderOpen className="w-5 h-5 mb-1" />
                                  <span className="text-xs">Gallery</span>
                                </button>
                                <label className="w-28 h-7 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-white text-neutral-400">
                                  <Upload className="w-3 h-3 mr-1" />
                                  <span className="text-xs">Upload</span>
                                  <input type="file" accept="image/*,video/*" className="hidden" onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  try {
                                    const response = await axios.post(`${API_URL}/api/admin/cms/upload-media?category=benefits`, formData, {
                                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                    });
                                    const existingImages = selectedItem.benefits_images || (selectedItem.benefits_image_url ? [{ id: 'primary', url: selectedItem.benefits_image_url, is_primary: true }] : []);
                                    const newImage = { id: Date.now().toString(), url: response.data.url, is_primary: existingImages.length === 0 };
                                    setSelectedItem(prev => ({ 
                                      ...prev, 
                                      benefits_images: [...existingImages, newImage],
                                      benefits_image_url: newImage.is_primary ? response.data.url : prev.benefits_image_url
                                    }));
                                  } catch (error) { toast.error('Failed to upload'); }
                                  e.target.value = '';
                                }} />
                              </label>
                            </div>
                            <p className="text-xs text-neutral-500 mt-2">Click ★ to set primary. Primary image shown on page.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    )}
                  </div>

                  {/* ========== CONFIGURATOR DEFAULTS ========== */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5" /> Configurator Defaults
                    </h3>
                    <p className="text-sm text-neutral-500 mb-4">
                      Set default fabric/material that will be pre-selected when customer opens the configurator from this product page.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Default Material ID</Label>
                        <Input
                          value={selectedItem.default_material || ''}
                          onChange={(e) => setSelectedItem(prev => ({ ...prev, default_material: e.target.value }))}
                          placeholder="e.g., light-filtering"
                          className="mt-1"
                        />
                        <p className="text-xs text-neutral-500 mt-1">Material ID from fabric options</p>
                      </div>
                      <div>
                        <Label>Default Color ID</Label>
                        <Input
                          value={selectedItem.default_color || ''}
                          onChange={(e) => setSelectedItem(prev => ({ ...prev, default_color: e.target.value }))}
                          placeholder="e.g., white"
                          className="mt-1"
                        />
                        <p className="text-xs text-neutral-500 mt-1">Color ID from fabric options</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Page Fields */}
              {selectedItem.type === 'page' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Page Name</Label>
                      <Input
                        value={selectedItem.name || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, name: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Path</Label>
                      <Input
                        value={selectedItem.path || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, path: e.target.value }))}
                        className="mt-1"
                        disabled={selectedItem.is_system}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedItem.is_active}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">Published</span>
                    </label>
                    {selectedItem.is_system && (
                      <span className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded">
                        System Page (cannot delete)
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* Category Fields */}
              {selectedItem.type === 'category' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category Name</Label>
                      <Input
                        value={selectedItem.name || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Shades"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Slug (URL path)</Label>
                      <Input
                        value={selectedItem.slug || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                        placeholder="e.g., shades"
                        className="mt-1"
                        disabled={!selectedItem.isNew}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <RichTextEditor
                      value={selectedItem.description || ''}
                      onChange={(html) => setSelectedItem(prev => ({ ...prev, description: html }))}
                      placeholder="Category description for the /products page..."
                      className="mt-1"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Use toolbar for formatting: bold, italic, lists, links</p>
                  </div>

                  {/* Category Image */}
                  <div>
                    <Label>Category Image</Label>
                    <div className="mt-2 flex items-start gap-4">
                      {selectedItem.image_url && (
                        <div className="relative group">
                          <img 
                            src={selectedItem.image_url.startsWith('/') ? `${API_URL}${selectedItem.image_url}` : selectedItem.image_url}
                            alt="Category"
                            className="w-32 h-24 object-cover rounded border cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => openImagePreview(
                              selectedItem.image_url.startsWith('/') ? `${API_URL}${selectedItem.image_url}` : selectedItem.image_url,
                              selectedItem.name
                            )}
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
                            <Eye className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      )}
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {/* AI Generate Button - Opens Full Modal */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowCategoryAIFullModal(true)}
                            disabled={!selectedItem.id}
                            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 hover:from-purple-600 hover:to-pink-600"
                            data-testid="category-ai-generate-btn"
                          >
                            <Sparkles className="w-4 h-4 mr-1" />
                            AI Generate
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openImageGallery((url) => {
                              setSelectedItem(prev => ({ ...prev, image_url: url }));
                            }, 'categories')}
                            data-testid="category-gallery-btn"
                          >
                            <FolderOpen className="w-4 h-4 mr-1" /> Gallery
                          </Button>
                          <label className="cursor-pointer">
                            <Button variant="outline" size="sm" disabled={uploadingImage} asChild>
                              <span>
                                {uploadingImage ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                                Upload
                              </span>
                            </Button>
                            <input
                              type="file"
                              accept="image/*,video/*"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setUploadingImage(true);
                                const formData = new FormData();
                                formData.append('file', file);
                                try {
                                  const response = await axios.post(`${API_URL}/api/admin/cms/upload-media?category=categories`, formData, {
                                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                  });
                                  setSelectedItem(prev => ({ ...prev, image_url: response.data.url }));
                                  toast.success('Image uploaded!');
                                } catch (error) {
                                  toast.error('Failed to upload image');
                                } finally {
                                  setUploadingImage(false);
                                  e.target.value = '';
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                          {selectedItem.image_url && (
                            <Button variant="ghost" size="sm" onClick={() => setSelectedItem(prev => ({ ...prev, image_url: '' }))}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        {!selectedItem.id && (
                          <p className="text-xs text-amber-600">Save the category first to enable AI generation</p>
                        )}
                        
                        <Input
                          value={selectedItem.image_url || ''}
                          onChange={(e) => setSelectedItem(prev => ({ ...prev, image_url: e.target.value }))}
                          placeholder="Or enter image URL..."
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Icon (Lucide icon name)</Label>
                      <Input
                        value={selectedItem.icon || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, icon: e.target.value }))}
                        placeholder="e.g., Sun, Layers, Zap"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Sort Order</Label>
                      <Input
                        type="number"
                        value={selectedItem.sort_order || 0}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedItem.is_active}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">Active (visible on site)</span>
                    </label>
                  </div>
                </>
              )}

              {/* SEO Section */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Globe className="w-5 h-5" /> SEO Settings
                  </h3>
                  <Button
                    variant="outline"
                    onClick={handleGenerateSEO}
                    disabled={generatingSEO}
                    data-testid="generate-seo-btn"
                  >
                    {generatingSEO ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Generate with AI
                  </Button>
                </div>

                <div className="space-y-4 bg-neutral-50 rounded-lg p-4">
                  <div>
                    <Label>Meta Title (max 60 chars)</Label>
                    <Input
                      value={selectedItem.seo?.meta_title || ''}
                      onChange={(e) => setSelectedItem(prev => ({
                        ...prev,
                        seo: { ...prev.seo, meta_title: e.target.value }
                      }))}
                      placeholder="Page title for search engines"
                      className="mt-1"
                      maxLength={60}
                    />
                    <p className="text-xs text-neutral-500 mt-1">
                      {(selectedItem.seo?.meta_title || '').length}/60 characters
                    </p>
                  </div>

                  <div>
                    <Label>Meta Description (max 155 chars)</Label>
                    <Textarea
                      value={selectedItem.seo?.meta_description || ''}
                      onChange={(e) => setSelectedItem(prev => ({
                        ...prev,
                        seo: { ...prev.seo, meta_description: e.target.value }
                      }))}
                      placeholder="Brief description for search results"
                      className="mt-1"
                      maxLength={155}
                    />
                    <p className="text-xs text-neutral-500 mt-1">
                      {(selectedItem.seo?.meta_description || '').length}/155 characters
                    </p>
                  </div>

                  <div>
                    <Label>Meta Keywords</Label>
                    <Input
                      value={selectedItem.seo?.meta_keywords || ''}
                      onChange={(e) => setSelectedItem(prev => ({
                        ...prev,
                        seo: { ...prev.seo, meta_keywords: e.target.value }
                      }))}
                      placeholder="comma, separated, keywords"
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>OG Title (social sharing)</Label>
                      <Input
                        value={selectedItem.seo?.og_title || ''}
                        onChange={(e) => setSelectedItem(prev => ({
                          ...prev,
                          seo: { ...prev.seo, og_title: e.target.value }
                        }))}
                        placeholder="Title for social media"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>OG Description</Label>
                      <Input
                        value={selectedItem.seo?.og_description || ''}
                        onChange={(e) => setSelectedItem(prev => ({
                          ...prev,
                          seo: { ...prev.seo, og_description: e.target.value }
                        }))}
                        placeholder="Description for social media"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={handlePreview}
                  disabled={selectedItem?.isNew}
                  data-testid="cms-preview-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview Page
                </Button>
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="cms-save-btn">
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Image Gallery Modal */}
      <ImageGalleryModal
        isOpen={showImageGallery}
        onClose={() => { setShowImageGallery(false); setGalleryCallback(null); setGalleryCategory('general'); }}
        onSelect={(url) => {
          if (galleryCallback) {
            galleryCallback(url);
          }
        }}
        token={token}
        defaultCategory={galleryCategory}
      />

      {/* AI Image Generation Modal - Enhanced with Style Presets & Batch */}
      <Dialog open={showAIImageModal} onOpenChange={setShowAIImageModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Generate AI Room Image
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-neutral-600">
              Generate realistic room visualizations featuring <strong>{selectedItem?.title || selectedItem?.product_type || 'this product'}</strong> on the windows.
            </p>
            
            {/* Style Presets */}
            <div>
              <Label className="text-sm font-medium">Quick Style Presets</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(stylePresets).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => applyStylePreset(key)}
                    className="text-left p-2 border rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors"
                    data-testid={`preset-${key}`}
                  >
                    <div className="font-medium text-sm">{preset.name}</div>
                    <div className="text-xs text-neutral-500 truncate">{preset.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <div>
                <Label htmlFor="ai-room-type">Room Type</Label>
                <select
                  id="ai-room-type"
                  value={aiImageSettings.room_type}
                  onChange={(e) => setAiImageSettings(prev => ({ ...prev, room_type: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-md bg-white"
                  data-testid="ai-room-type-select"
                >
                  <option value="living room">Living Room</option>
                  <option value="bedroom">Bedroom</option>
                  <option value="dining room">Dining Room</option>
                  <option value="home office">Home Office</option>
                  <option value="kitchen">Kitchen</option>
                  <option value="bathroom">Bathroom</option>
                  <option value="nursery">Nursery</option>
                  <option value="sunroom">Sunroom</option>
                </select>
              </div>

              <div>
                <Label htmlFor="ai-style">Interior Style</Label>
                <select
                  id="ai-style"
                  value={aiImageSettings.style}
                  onChange={(e) => setAiImageSettings(prev => ({ ...prev, style: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-md bg-white"
                  data-testid="ai-style-select"
                >
                  <option value="modern">Modern</option>
                  <option value="traditional">Traditional</option>
                  <option value="minimalist">Minimalist</option>
                  <option value="luxurious">Luxurious</option>
                  <option value="contemporary">Contemporary</option>
                  <option value="farmhouse">Farmhouse</option>
                  <option value="coastal">Coastal</option>
                  <option value="scandinavian">Scandinavian</option>
                  <option value="industrial loft">Industrial Loft</option>
                  <option value="bohemian eclectic">Bohemian Eclectic</option>
                  <option value="japandi">Japandi</option>
                </select>
              </div>

              <div>
                <Label htmlFor="ai-color">Color Scheme (optional)</Label>
                <Input
                  id="ai-color"
                  placeholder="e.g., white, beige, navy blue"
                  value={aiImageSettings.color_scheme}
                  onChange={(e) => setAiImageSettings(prev => ({ ...prev, color_scheme: e.target.value }))}
                  data-testid="ai-color-input"
                />
                <p className="text-xs text-neutral-500 mt-1">Leave empty for AI to choose colors based on style</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                <strong>Current Settings:</strong> {aiImageSettings.style} {aiImageSettings.room_type}
                {aiImageSettings.color_scheme && ` with ${aiImageSettings.color_scheme}`}
              </p>
            </div>

            {/* Quick Actions Row */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTemplatesModal(true)}
                className="text-purple-600 border-purple-200"
              >
                <LayoutTemplate className="w-4 h-4 mr-1" />
                Room Templates
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPromptHistoryModal(true)}
                className="text-purple-600 border-purple-200"
              >
                <History className="w-4 h-4 mr-1" />
                Saved Prompts
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const name = prompt('Save current settings as:', `${aiImageSettings.style} ${aiImageSettings.room_type}`);
                  if (name) handleSavePrompt(name);
                }}
                className="text-purple-600 border-purple-200"
              >
                <Heart className="w-4 h-4 mr-1" />
                Save Settings
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-2 border-t">
            {/* Auto-Variations Button */}
            <Button
              variant="outline"
              onClick={handleGenerateAutoVariations}
              disabled={generatingAIImage || generatingBatch || generatingAutoVariations || !selectedItem?.id}
              className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="auto-variations-btn"
            >
              {generatingAutoVariations ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating 3 Variations...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Auto-Generate 3 Variations (Compare & Pick)
                </>
              )}
            </Button>

            <div className="flex justify-between gap-2">
              <Button
                variant="outline"
                onClick={() => setShowAIImageModal(false)}
                disabled={generatingAIImage || generatingBatch || generatingAutoVariations}
              >
                Cancel
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleBatchGeneration}
                  disabled={generatingAIImage || generatingBatch || generatingAutoVariations || !selectedItem?.id}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="batch-generate-btn"
                >
                  {generatingBatch ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Layers className="w-4 h-4 mr-2" />
                      Set (3)
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleGenerateAIImage}
                  disabled={generatingAIImage || generatingBatch || generatingAutoVariations || !selectedItem?.id}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                  data-testid="confirm-generate-ai-image-btn"
                >
                  {generatingAIImage ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate 1
                    </>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-center text-neutral-500">
              "Set" = Front View + Detail + Lifestyle | "Auto-Variations" = 3 moods to compare
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category AI Image Generation Modal */}
      <Dialog open={showCategoryAIModal} onOpenChange={setShowCategoryAIModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Generate Category Hero Image
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-neutral-600">
              Generate a hero image for the <strong>{selectedCategoryForAI?.name || 'category'}</strong> showcasing its products.
            </p>
            
            {/* Quick Presets for Category */}
            <div>
              <Label className="text-sm font-medium">Quick Presets</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(stylePresets).slice(0, 4).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => applyStylePreset(key)}
                    className="text-left p-2 border rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors"
                  >
                    <div className="font-medium text-xs">{preset.name}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label>Room Type</Label>
                <select
                  value={aiImageSettings.room_type}
                  onChange={(e) => setAiImageSettings(prev => ({ ...prev, room_type: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-md bg-white"
                >
                  <option value="living room">Living Room</option>
                  <option value="bedroom">Bedroom</option>
                  <option value="dining room">Dining Room</option>
                  <option value="home office">Home Office</option>
                </select>
              </div>
              <div>
                <Label>Style</Label>
                <select
                  value={aiImageSettings.style}
                  onChange={(e) => setAiImageSettings(prev => ({ ...prev, style: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-md bg-white"
                >
                  <option value="modern">Modern</option>
                  <option value="traditional">Traditional</option>
                  <option value="minimalist">Minimalist</option>
                  <option value="luxurious">Luxurious</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCategoryAIModal(false)} disabled={generatingAIImage}>
              Cancel
            </Button>
            <Button
              onClick={handleCategoryAIGeneration}
              disabled={generatingAIImage}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            >
              {generatingAIImage ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate Hero Image</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prompt History Modal */}
      <Dialog open={showPromptHistoryModal} onOpenChange={setShowPromptHistoryModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-500" />
              Saved Prompts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {promptHistory.length === 0 ? (
              <p className="text-center text-neutral-500 py-8">
                No saved prompts yet. Save your favorite settings to reuse them later.
              </p>
            ) : (
              <>
                {/* Favorites first */}
                {promptHistory.filter(p => p.is_favorite).length > 0 && (
                  <div className="mb-4">
                    <Label className="text-xs text-neutral-500 uppercase tracking-wide">Favorites</Label>
                    <div className="space-y-2 mt-2">
                      {promptHistory.filter(p => p.is_favorite).map(prompt => (
                        <div key={prompt.id} className="flex items-center justify-between p-3 border rounded-lg bg-yellow-50 border-yellow-200">
                          <div className="flex-1">
                            <div className="font-medium text-sm flex items-center gap-2">
                              <Heart className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              {prompt.name}
                            </div>
                            <div className="text-xs text-neutral-500">
                              {prompt.settings?.style} {prompt.settings?.room_type}
                              {prompt.use_count > 0 && ` • Used ${prompt.use_count}x`}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleUsePrompt(prompt)}>
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleToggleFavorite(prompt.id)}>
                              <Heart className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeletePrompt(prompt.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Other prompts */}
                <div>
                  <Label className="text-xs text-neutral-500 uppercase tracking-wide">All Prompts</Label>
                  <div className="space-y-2 mt-2">
                    {promptHistory.filter(p => !p.is_favorite).map(prompt => (
                      <div key={prompt.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-neutral-50">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{prompt.name}</div>
                          <div className="text-xs text-neutral-500">
                            {prompt.settings?.style} {prompt.settings?.room_type}
                            {prompt.use_count > 0 && ` • Used ${prompt.use_count}x`}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleUsePrompt(prompt)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleToggleFavorite(prompt.id)}>
                            <Heart className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeletePrompt(prompt.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowPromptHistoryModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Templates Modal */}
      <Dialog open={showTemplatesModal} onOpenChange={setShowTemplatesModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-purple-500" />
              Room Templates
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-neutral-600 mb-4">
              Choose a pre-designed room template to generate stunning images quickly.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(roomTemplates).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => handleGenerateFromTemplate(template)}
                  disabled={generatingAIImage}
                  className="text-left p-4 border rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all hover:shadow-md disabled:opacity-50"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      {template.room_type === 'living room' && <Home className="w-5 h-5 text-purple-600" />}
                      {template.room_type === 'bedroom' && <Moon className="w-5 h-5 text-purple-600" />}
                      {template.room_type === 'home office' && <Monitor className="w-5 h-5 text-purple-600" />}
                      {template.room_type === 'dining room' && <Building className="w-5 h-5 text-purple-600" />}
                      {template.room_type === 'nursery' && <Heart className="w-5 h-5 text-purple-600" />}
                      {template.room_type === 'sunroom' && <Sun className="w-5 h-5 text-purple-600" />}
                      {template.room_type === 'loft' && <Building className="w-5 h-5 text-purple-600" />}
                      {template.room_type === 'bathroom' && <Droplets className="w-5 h-5 text-purple-600" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{template.name}</div>
                      <div className="text-xs text-neutral-500 mt-1">{template.description}</div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {template.preview_keywords?.map((kw, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-neutral-100 rounded-full text-neutral-600">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowTemplatesModal(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Comparison Modal */}
      <Dialog open={showComparisonModal} onOpenChange={setShowComparisonModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-500" />
              Compare & Select Variation
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-neutral-600 mb-4">
              Choose your favorite variation to add to the product images.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {comparisonImages.map((variation, idx) => (
                <div key={idx} className="border rounded-xl overflow-hidden hover:border-purple-400 transition-all hover:shadow-lg">
                  <div className="aspect-square relative group cursor-pointer" onClick={() => openImagePreview(
                    variation.url.startsWith('/') ? `${API_URL}${variation.url}` : variation.url,
                    variation.name,
                    variation.media?.id
                  )}>
                    <img 
                      src={variation.url.startsWith('/') ? `${API_URL}${variation.url}` : variation.url} 
                      alt={variation.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Eye className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="p-3 bg-white">
                    <div className="font-medium text-sm text-center mb-2">{variation.name}</div>
                    <Button
                      onClick={() => handleSelectVariation(variation)}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                      size="sm"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Select This
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowComparisonModal(false);
                setComparisonImages([]);
              }}
            >
              Cancel (Discard All)
            </Button>
            <p className="text-xs text-neutral-500 self-center">
              Click "Select This" to add your chosen variation
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* FULL Category AI Image Generation Modal - Same as Products */}
      <Dialog open={showCategoryAIFullModal} onOpenChange={setShowCategoryAIFullModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Generate AI Image for {selectedItem?.name || 'Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-neutral-600">
              Generate a stunning hero image showcasing <strong>{selectedItem?.name || 'this category'}</strong> window treatments.
            </p>
            
            {/* Style Presets */}
            <div>
              <Label className="text-sm font-medium">Quick Style Presets</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(stylePresets).slice(0, 6).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => applyStylePreset(key)}
                    className="text-left p-2 border rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors"
                  >
                    <div className="font-medium text-sm">{preset.name}</div>
                    <div className="text-xs text-neutral-500 truncate">{preset.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <div>
                <Label htmlFor="cat-ai-room-type">Room Type</Label>
                <select
                  id="cat-ai-room-type"
                  value={aiImageSettings.room_type}
                  onChange={(e) => setAiImageSettings(prev => ({ ...prev, room_type: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-md bg-white"
                >
                  <option value="living room">Living Room</option>
                  <option value="bedroom">Bedroom</option>
                  <option value="dining room">Dining Room</option>
                  <option value="home office">Home Office</option>
                  <option value="kitchen">Kitchen</option>
                  <option value="bathroom">Bathroom</option>
                  <option value="nursery">Nursery</option>
                  <option value="sunroom">Sunroom</option>
                </select>
              </div>

              <div>
                <Label htmlFor="cat-ai-style">Interior Style</Label>
                <select
                  id="cat-ai-style"
                  value={aiImageSettings.style}
                  onChange={(e) => setAiImageSettings(prev => ({ ...prev, style: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-md bg-white"
                >
                  <option value="modern">Modern</option>
                  <option value="traditional">Traditional</option>
                  <option value="minimalist">Minimalist</option>
                  <option value="luxurious">Luxurious</option>
                  <option value="contemporary">Contemporary</option>
                  <option value="farmhouse">Farmhouse</option>
                  <option value="coastal">Coastal</option>
                  <option value="scandinavian">Scandinavian</option>
                  <option value="industrial loft">Industrial Loft</option>
                  <option value="bohemian eclectic">Bohemian Eclectic</option>
                </select>
              </div>

              <div>
                <Label htmlFor="cat-ai-color">Color Scheme (optional)</Label>
                <Input
                  id="cat-ai-color"
                  placeholder="e.g., white, beige, navy blue"
                  value={aiImageSettings.color_scheme}
                  onChange={(e) => setAiImageSettings(prev => ({ ...prev, color_scheme: e.target.value }))}
                />
                <p className="text-xs text-neutral-500 mt-1">Leave empty for AI to choose colors</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                <strong>Current Settings:</strong> {aiImageSettings.style} {aiImageSettings.room_type}
                {aiImageSettings.color_scheme && ` with ${aiImageSettings.color_scheme}`}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-2 border-t">
            {/* Auto-Variations Button */}
            <Button
              variant="outline"
              onClick={handleGenerateCategoryAutoVariations}
              disabled={generatingCategoryAI || generatingCategoryAutoVar}
              className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              {generatingCategoryAutoVar ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating 3 Variations...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Auto-Generate 3 Variations (Compare & Pick)
                </>
              )}
            </Button>

            <div className="flex justify-between gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCategoryAIFullModal(false)}
                disabled={generatingCategoryAI || generatingCategoryAutoVar}
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerateCategoryAIImage}
                disabled={generatingCategoryAI || generatingCategoryAutoVar}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
              >
                {generatingCategoryAI ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate 1 Image
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-center text-neutral-500">
              Use "3 Variations" to compare different moods before selecting
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Comparison Modal */}
      <Dialog open={showCategoryComparisonModal} onOpenChange={setShowCategoryComparisonModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-500" />
              Compare & Select Category Image
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-neutral-600 mb-4">
              Choose the best image for your category. Click to enlarge.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {categoryComparisonImages.map((variation, idx) => (
                <div key={idx} className="border rounded-xl overflow-hidden hover:border-purple-400 transition-all hover:shadow-lg">
                  <div className="aspect-square relative group cursor-pointer" onClick={() => openImagePreview(
                    variation.url.startsWith('/') ? `${API_URL}${variation.url}` : variation.url,
                    variation.name,
                    variation.media?.id
                  )}>
                    <img 
                      src={variation.url.startsWith('/') ? `${API_URL}${variation.url}` : variation.url} 
                      alt={variation.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Eye className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="p-3 bg-white">
                    <div className="font-medium text-sm text-center mb-2">{variation.name}</div>
                    <Button
                      onClick={() => handleSelectCategoryVariation(variation)}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                      size="sm"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Use This
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCategoryComparisonModal(false);
                setCategoryComparisonImages([]);
              }}
            >
              Cancel (Discard All)
            </Button>
            <p className="text-xs text-neutral-500 self-center">
              Click "Use This" to set as category image
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal (Enlarged View) */}
      <Dialog open={showImagePreviewModal} onOpenChange={setShowImagePreviewModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] p-0 overflow-hidden bg-black">
          <DialogHeader className="sr-only">
            <DialogTitle>{previewImage?.alt || 'Image Preview'}</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowImagePreviewModal(false)}
              className="absolute top-4 right-4 z-10 bg-black/50 text-white hover:bg-black/70 rounded-full p-2"
            >
              <X className="w-5 h-5" />
            </Button>
            {previewImage && (
              <img 
                src={previewImage.url} 
                alt={previewImage.alt}
                className="w-full h-auto max-h-[75vh] object-contain"
              />
            )}
          </div>
          <div className="bg-black/90 text-white p-4">
            <p className="text-sm text-center mb-3">{previewImage?.alt}</p>
            {previewImage?.media_id && (
              <div className="flex justify-center gap-3">
                <Button
                  size="sm"
                  variant="secondary"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={async () => {
                    const success = await handleToggleGallery(previewImage.media_id, true, {
                      title: previewImage.alt || 'Design Showcase',
                      description: `${selectedItem?.title || 'Window treatment'} visualization`,
                      category: 'residential',
                      product: selectedItem?.product_type || 'roller-shades'
                    });
                    if (success) {
                      setShowImagePreviewModal(false);
                    }
                  }}
                >
                  <ImageIcon className="w-4 h-4 mr-1" />
                  Add to Gallery
                </Button>
                <a 
                  href={previewImage.url} 
                  download 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-sm bg-neutral-700 hover:bg-neutral-600 text-white rounded-md"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </a>
              </div>
            )}
            <p className="text-xs text-neutral-400 mt-3 text-center">Click outside or press ESC to close</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSTab;
